import React, { useEffect, useMemo, useState } from 'react';
import { BaseButton, useT } from '@repo/common';
import {
  createPathFeedSlide,
  deletePathFeedSlide,
  getPathFeedSlideImageUploadUrl,
  getPathFeedSlides,
  updatePathFeedSlide,
  uploadPathFeedSlideImage,
} from '../../../api/workshop/pathFeedSlide.api';

const INPUT_CLASS =
  'w-full rounded-lg border border-grayscale-gray03 bg-white px-4 py-3 typo-b14 text-text-primary outline-none focus:border-green-deep02';
const EMPTY_FORM = {
  id: null,
  title: '',
  description: '',
  imageUrl: '',
  displayOrder: 0,
  active: true,
};

const HeroSlideManager = ({ fallbackImage, onClose, onChanged }) => {
  const t = useT();
  const [slides, setSlides] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedIndex = useMemo(
    () => slides.findIndex((slide) => slide.id === form.id),
    [form.id, slides],
  );

  const loadSlides = async (selectId) => {
    try {
      setLoading(true);
      const data = await getPathFeedSlides({ includeInactive: true });
      setSlides(data);
      if (selectId) {
        const selected = data.find((slide) => slide.id === selectId);
        if (selected) selectSlide(selected);
      }
    } catch (loadError) {
      setError(
        loadError.response?.data?.msg ||
          t('The slides could not be loaded.', '슬라이드를 불러오지 못했습니다.'),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlides();
  }, []);

  useEffect(
    () => () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  const selectSlide = (slide) => {
    setForm({
      id: slide.id,
      title: slide.title || '',
      description: slide.description || '',
      imageUrl: slide.imageUrl || '',
      displayOrder: slide.displayOrder ?? 0,
      active: slide.active !== false,
    });
    setImageFile(null);
    setImagePreview(slide.imageUrl || '');
    setError('');
  };

  const startCreate = () => {
    setForm({ ...EMPTY_FORM, displayOrder: slides.length });
    setImageFile(null);
    setImagePreview('');
    setError('');
  };

  const handleImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
      setError(t('Use an image file up to 10 MB.', '10MB 이하의 이미지 파일을 사용해 주세요.'));
      return;
    }
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const saveSlide = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError(t('Enter a slide title.', '슬라이드 제목을 입력해 주세요.'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      let imageUrl = form.imageUrl;
      if (imageFile) {
        const upload = await getPathFeedSlideImageUploadUrl(imageFile.name);
        await uploadPathFeedSlideImage(imageFile, upload.uploadUrl);
        imageUrl = upload.key;
      }

      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        imageUrl,
        displayOrder: Number(form.displayOrder),
        active: form.active,
      };
      const saved = form.id
        ? await updatePathFeedSlide(form.id, payload)
        : await createPathFeedSlide(payload);
      await loadSlides(saved.id);
      await onChanged();
    } catch (saveError) {
      setError(
        saveError.response?.data?.msg ||
          t('The slide could not be saved.', '슬라이드를 저장하지 못했습니다.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const removeSlide = async () => {
    if (!form.id) return;
    if (!window.confirm(t('Delete this PathFeed slide?', '이 PathFeed 슬라이드를 삭제할까요?'))) return;

    try {
      setSaving(true);
      await deletePathFeedSlide(form.id);
      startCreate();
      await loadSlides();
      await onChanged();
    } catch (deleteError) {
      setError(
        deleteError.response?.data?.msg ||
          t('The slide could not be deleted.', '슬라이드를 삭제하지 못했습니다.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const moveSlide = async (direction) => {
    const targetIndex = selectedIndex + direction;
    if (selectedIndex < 0 || targetIndex < 0 || targetIndex >= slides.length) {
      return;
    }

    const current = slides[selectedIndex];
    const target = slides[targetIndex];
    try {
      setSaving(true);
      await Promise.all([
        updatePathFeedSlide(current.id, { displayOrder: target.displayOrder }),
        updatePathFeedSlide(target.id, { displayOrder: current.displayOrder }),
      ]);
      await loadSlides(current.id);
      await onChanged();
    } catch (moveError) {
      setError(
        moveError.response?.data?.msg ||
          t('The slide order could not be changed.', '슬라이드 순서를 변경하지 못했습니다.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className='fixed inset-0 z-[10000] bg-surface-black40'
        onClick={onClose}
      />
      <div className='fixed inset-0 z-[10001] flex items-center justify-center p-6'>
        <div
          className='flex h-[min(780px,92vh)] w-full max-w-[1120px] overflow-hidden rounded-[30px] bg-grayscale-gray01 shadow-popup-01'
          onClick={(event) => event.stopPropagation()}
        >
          <aside className='flex w-[360px] shrink-0 flex-col border-r border-grayscale-gray02 bg-white'>
            <div className='flex items-center justify-between border-b border-grayscale-gray02 p-5'>
              <div>
                <p className='typo-title2 text-text-primary'>
                  {t('Hero slides', '히어로 슬라이드')}
                </p>
                <p className='mt-1 typo-b12 text-text-secondary'>
                  {t(`${slides.length} slides`, `슬라이드 ${slides.length}개`)}
                </p>
              </div>
              <BaseButton
                type='primary'
                size='m'
                htmlType='button'
                hideIcon
                typo={false}
                onClick={startCreate}
                className='h-auto min-w-0 rounded-lg border-0 bg-green-deep02 px-4 py-2 typo-b14Strong text-white'
              >
                {t('+ Add', '+ 추가')}
              </BaseButton>
            </div>
            <div className='flex-1 overflow-y-auto p-3'>
              {loading ? (
                <p className='p-4 typo-b14 text-text-secondary'>{t('Loading...', '불러오는 중...')}</p>
              ) : (
                slides.map((slide, index) => (
                  <BaseButton
                    key={slide.id}
                    type='text'
                    size='m'
                    htmlType='button'
                    typo={false}
                    leftalign
                    gap='gap-3'
                    onClick={() => selectSlide(slide)}
                    className={`mb-2 h-auto w-full min-w-0 rounded-xl border-0 p-3 text-left ${
                      form.id === slide.id
                        ? 'bg-green-light01 hover:bg-green-light01'
                        : 'bg-transparent hover:bg-grayscale-gray01'
                    }`}
                    textWrapperClass='h-auto'
                    icon={
                      <img
                        src={slide.imageUrl || fallbackImage}
                        alt=''
                        className='h-16 w-20 shrink-0 rounded-lg object-cover'
                      />
                    }
                  >
                    <span className='block min-w-0'>
                      <span className='block typo-b12 text-text-secondary'>
                        {t(`Slide ${index + 1}`, `슬라이드 ${index + 1}`)}
                        {!slide.active ? t(' · Hidden', ' · 숨김') : ''}
                      </span>
                      <span className='mt-1 block line-clamp-2 typo-b14Strong text-text-primary'>
                        {slide.title}
                      </span>
                    </span>
                  </BaseButton>
                ))
              )}
            </div>
          </aside>

          <form onSubmit={saveSlide} className='flex min-w-0 flex-1 flex-col'>
            <header className='flex items-center justify-between border-b border-grayscale-gray02 bg-white px-6 py-5'>
              <p className='typo-title2 text-text-primary'>
                {form.id
                  ? t('Edit slide', '슬라이드 수정')
                  : t('Create slide', '슬라이드 만들기')}
              </p>
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                hideIcon
                typo={false}
                onClick={onClose}
                className='h-auto min-w-0 rounded-lg border-0 bg-transparent px-3 py-2 typo-b14Strong text-text-secondary hover:bg-grayscale-gray02 hover:text-text-secondary'
              >
                {t('Close', '닫기')}
              </BaseButton>
            </header>

            <div className='flex-1 overflow-y-auto p-6'>
              {error && (
                <p className='mb-4 rounded-lg bg-functional-failed01 px-4 py-3 typo-b14 text-functional-failed03'>
                  {error}
                </p>
              )}
              <label className='block'>
                <span className='mb-2 block typo-b14Strong'>{t('Title *', '제목 *')}</span>
                <input
                  value={form.title}
                  maxLength={200}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }))
                  }
                  className={INPUT_CLASS}
                />
              </label>
              <label className='mt-5 block'>
                <span className='mb-2 block typo-b14Strong'>{t('Description', '설명')}</span>
                <textarea
                  value={form.description}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      description: event.target.value,
                    }))
                  }
                  className={`${INPUT_CLASS} min-h-[120px] resize-y`}
                />
              </label>

              <div className='mt-5'>
                <span className='mb-2 block typo-b14Strong'>{t('Slide image', '슬라이드 이미지')}</span>
                <div className='overflow-hidden rounded-xl bg-grayscale-gray02'>
                  <img
                    src={imagePreview || fallbackImage}
                    alt={t('Slide preview', '슬라이드 미리보기')}
                    className='aspect-[16/7] w-full object-cover'
                  />
                </div>
                <label className='mt-3 inline-flex cursor-pointer rounded-lg border border-green-deep02 px-4 py-2 typo-b14Strong text-green-deep02'>
                  {t('Choose image', '이미지 선택')}
                  <input
                    type='file'
                    accept='image/*'
                    className='sr-only'
                    onChange={handleImage}
                    disabled={saving}
                  />
                </label>
              </div>

              <div className='mt-5 flex items-center justify-between rounded-xl bg-white p-4'>
                <label className='flex cursor-pointer items-center gap-2 typo-b14Strong'>
                  <input
                    type='checkbox'
                    checked={form.active}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        active: event.target.checked,
                      }))
                    }
                    className='size-4 accent-green-deep02'
                  />
                  {t('Visible on PathFeed', 'PathFeed에 노출')}
                </label>
                {form.id && (
                  <div className='flex gap-2'>
                    <BaseButton
                      type='text'
                      size='m'
                      htmlType='button'
                      hideIcon
                      typo={false}
                      onClick={() => moveSlide(-1)}
                      isDisabled={saving || selectedIndex <= 0}
                      disabledTextClass='text-text-primary opacity-30'
                      disabledBorderClass='border-grayscale-gray03'
                      className='h-auto min-w-0 rounded-lg border border-grayscale-gray03 bg-transparent px-3 py-2 typo-b14Strong'
                    >
                      {t('Move up', '위로 이동')}
                    </BaseButton>
                    <BaseButton
                      type='text'
                      size='m'
                      htmlType='button'
                      hideIcon
                      typo={false}
                      onClick={() => moveSlide(1)}
                      isDisabled={
                        saving || selectedIndex === slides.length - 1
                      }
                      disabledTextClass='text-text-primary opacity-30'
                      disabledBorderClass='border-grayscale-gray03'
                      className='h-auto min-w-0 rounded-lg border border-grayscale-gray03 bg-transparent px-3 py-2 typo-b14Strong'
                    >
                      {t('Move down', '아래로 이동')}
                    </BaseButton>
                  </div>
                )}
              </div>
            </div>

            <footer className='flex justify-between border-t border-grayscale-gray02 bg-white p-5'>
              <BaseButton
                type='text'
                size='m'
                htmlType='button'
                hideIcon
                typo={false}
                onClick={removeSlide}
                isDisabled={!form.id || saving}
                disabledTextClass='text-functional-failed03'
                className='h-auto min-w-0 rounded-lg border-0 bg-transparent px-5 py-3 typo-b14Strong text-functional-failed03 hover:text-functional-failed03 disabled:invisible'
              >
                {t('Delete', '삭제')}
              </BaseButton>
              <BaseButton
                type='primary'
                size='m'
                htmlType='submit'
                hideIcon
                typo={false}
                isDisabled={saving}
                disabledBgClass='bg-green-deep02 opacity-50'
                className='min-w-[140px] h-auto rounded-lg border-0 bg-green-deep02 px-6 py-3 typo-b14Strong text-white'
              >
                {saving
                  ? t('Saving...', '저장 중...')
                  : form.id
                    ? t('Save changes', '변경 사항 저장')
                    : t('Create', '만들기')}
              </BaseButton>
            </footer>
          </form>
        </div>
      </div>
    </>
  );
};

export default HeroSlideManager;
